import { useEffect, useState } from "react";
import { Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { Field } from "@/components/music/FormBits";
import { FilePick } from "@/components/music/UploadForm";
import { VIDEO_CATEGORIES } from "./videoConstants";

export const VideoEditDialog = ({ video, onClose, onSaved }) => {
  const { request } = useProfile();
  const [form, setForm] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (video) { setForm({ title: video.title, category: video.category, description: video.description }); setThumb(null); } }, [video]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let updated = await request("patch", `/videos/${video.id}`, form);
      if (thumb) { const fd = new FormData(); fd.append("thumbnail", thumb); updated = await request("post", `/videos/${video.id}/thumbnail`, fd); }
      toast.success("Video updated");
      onSaved(updated);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="video-edit-dialog" className="bg-nw-bg2 border-white/10 text-slate-100 max-w-[92vw] sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl chrome-text">Edit video</DialogTitle>
          <DialogDescription className="text-slate-400">Update your video details.</DialogDescription>
        </DialogHeader>
        {form && (
          <form onSubmit={save} className="space-y-4">
            <Field label="Title"><input data-testid="video-edit-title-input" required maxLength={100} value={form.title} onChange={set("title")} className="nw-input" /></Field>
            <Field label="Category">
              <select data-testid="video-edit-category-select" value={form.category} onChange={set("category")} className="nw-input appearance-none">
                {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description"><textarea data-testid="video-edit-description-input" rows={3} maxLength={2000} value={form.description} onChange={set("description")} className="nw-input resize-none" /></Field>
            <FilePick testId="video-edit-thumbnail-input" accept="image/jpeg,image/png,image/webp" icon={ImagePlus} label="Replace thumbnail (optional)" file={thumb} onPick={setThumb} />
            <div className="flex gap-3 pt-2">
              <button type="button" data-testid="video-edit-cancel-button" onClick={onClose} className="btn-chrome flex-1 py-3 text-sm">Cancel</button>
              <button data-testid="video-edit-save-button" disabled={busy} className="btn-red flex-1 py-3 text-sm disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Save"}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
